import Geolocation from "@react-native-community/geolocation";
import haversine from "haversine-distance";
import { STRINGS } from "../constants";
import { convertMetersToMiles } from "./formatting";

/**
 * Retrieves the current geographical location of the device.
 *
 * @returns {Promise<{latitude: number, longitude: number}>} A promise that resolves to an object containing the latitude and longitude of the current location.
 * @throws {Error} If the location cannot be retrieved, the promise is rejected with an error.
 */
export const getCurrentLocation = async () => {
  return new Promise((resolve, reject) => {
    Geolocation.getCurrentPosition(
      (position) => {
        resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      },
      (error) => {
        reject(error);
      }
    );
  });
};

/**
 * Calculates the distance from the current location to a specified location.
 *
 * @param {Object} location - The target location to calculate the distance to.
 * @param {number} location.lat - The latitude of the target location.
 * @param {number} location.lng - The longitude of the target location.
 * @returns {Promise<string|null>} The distance in miles, rounded to one decimal place, or null if an error occurs.
 */
export const getDistanceFromLocation = async (userLocation, location) => {
  try {
    const distanceMeters = haversine(userLocation, { latitude: location.lat, longitude: location.lng });
    return (distanceMeters * 0.000621371).toFixed(1);
  } catch (error) {
    return null;
  }
};

/**
 * Calculates the distances of a list of locations from the user's current location.
 *
 * @async
 * @function
 * @param {Array<Object>} listOfLocations - An array of location objects. Each object should have either:
 *   - `geometry.y` and `geometry.x` properties, or
 *   - `latitude` and `longitude` properties.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of objects, where each object contains:
 *   - `id` (string|number): The ID of the location.
 *   - `distance` (number): The calculated distance from the user's location.
 * @throws Will log an error message if the user's location cannot be retrieved or if distance calculation fails.
 */
export const getDistancesOfLocationsFromUser = async (listOfLocations) => {
  try {
    const userLocation = await getCurrentLocation();
    const distances = await Promise.all(
      listOfLocations.map(async (loc) => {
        const dist = await getDistanceFromLocation(userLocation, {
          // Some data has location.geometry.{x,y} and some have location.latitude, location.longitude
          lat: loc.latitude != undefined ? loc.latitude : loc.geometry.y,
          lng: loc.longitude != undefined ? loc.longitude : loc.geometry.x,
        });
        return { ...loc, distance: dist };
      })
    );

    return distances;
  } catch (err) {
    return null;
  }
};

/**
 * Sorts an array of location objects by their distance property in ascending order.
 *
 * @param {Array<Object>} distances - An array of objects where each object contains a `distance` property.
 * @param {number} distances[].distance - The distance value of the location.
 * @returns {Array<Object>} The sorted array of location objects.
 */
export const sortDistancesOfLocations = (distances) => {
  return distances.sort((a, b) => a.distance - b.distance);
};

/**
 * Get nearby locations within a specified distance.
 *
 * @param {Object} location - The reference location with latitude and longitude.
 * @param {Array} listToCheck - The list of locations to check against.
 * @param {number} distanceInMiles - The maximum distance in miles to consider.
 * @param {number} itemsToShow - The number of nearby locations to return.
 * @returns {Array} - A list of nearby locations within the specified distance.
 */
export const getNearbyLocations = (location, listToCheck, distanceInMiles, itemsToShow) => {
  let res = [];
  listToCheck.filter((item) => {
    const distance = convertMetersToMiles(
      haversine(
        { latitude: location.latitude, longitude: location.longitude },
        { latitude: item.latitude, longitude: item.longitude }
      ),
      1
    );
    if (distance <= distanceInMiles) {
      res.push({ item: item, distance: distance });
    }
  });
  res.sort((a, b) => a.distance - b.distance);
  res = res.slice(0, itemsToShow);
  return res.map(({ item }) => item);
};
